/**
 * Generates random, workflow-themed names for new workflows.
 * Combines adjectives with workflow-related nouns to create memorable names.
 *
 * @returns A randomly generated workflow name (e.g., "Turbo Pipeline", "Swift Automation")
 *
 * @example
 * generateWorkflowName() // => "Lightning Processor"
 * generateWorkflowName() // => "Quantum Orchestrator"
 */
export const generateWorkflowName = (): string => {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  return `${adjective} ${noun}`;
};

// Adjectives - speed, intelligence, power, automation
const ADJECTIVES = [
  // Speed & Efficiency
  'Turbo',
  'Swift',
  'Rapid',
  'Speedy',
  'Lightning',
  'Quick',
  'Agile',
  'Nimble',
  'Express',
  'Instant',
  // Intelligence & Strategy
  'Smart',
  'Clever',
  'Brilliant',
  'Strategic',
  'Savvy',
  'Sharp',
  'Astute',
  'Insightful',
  'Intuitive',
  'Adaptive',
  // Power & Impact
  'Mighty',
  'Powerful',
  'Dynamic',
  'Robust',
  'Unstoppable',
  'Relentless',
  'Tireless',
  'Epic',
  'Ultimate',
  'Supreme',
  // Automation & Tech
  'Seamless',
  'Streamlined',
  'Optimized',
  'Synced',
  'Connected',
  'Integrated',
  'Unified',
  'Orchestrated',
  'Automated',
  'Parallel',
  // Positive Energy
  'Stellar',
  'Golden',
  'Prime',
  'Elite',
  'Super',
  'Mega',
  'Ultra',
  'Hyper',
  'Cosmic',
  'Quantum',
];

// Nouns - workflow concepts, documents, operations, productivity
const NOUNS = [
  // Core Workflow Concepts
  'Pipeline',
  'Workflow',
  'Automation',
  'Process',
  'Sequence',
  'Chain',
  'Flow',
  'Stream',
  'Circuit',
  'Loop',
  // Actions & Operations
  'Engine',
  'Machine',
  'Processor',
  'Generator',
  'Executor',
  'Orchestrator',
  'Dispatcher',
  'Handler',
  'Runner',
  'Builder',
  // Data & Intelligence
  'Analyzer',
  'Synthesizer',
  'Transformer',
  'Aggregator',
  'Curator',
  'Harvester',
  'Extractor',
  'Compiler',
  'Parser',
  'Mapper',
  // Productivity Metaphors
  'Rocket',
  'Launchpad',
  'Catalyst',
  'Accelerator',
  'Booster',
  'Turbocharger',
  'Powerhouse',
  'Dynamo',
  'Forge',
  'Factory',
  // Architecture
  'Architect',
  'Conductor',
  'Navigator',
  'Pilot',
  'Maestro',
  // Document & Spreadsheet Tools
  'Spreadsheet',
  'Document',
  'Report',
  'Dashboard',
  'Template',
  'Formatter',
  'Editor',
  'Publisher',
  'Composer',
  'Scribe',
  // Presentation & Visual
  'Presenter',
  'Slideshow',
  'Canvas',
  'Storyboard',
  'Visualizer',
  // Redaction & Security
  'Redactor',
  'Sanitizer',
  'Scrubber',
  'Censor',
  'Shield',
  // Data Operations
  'Merger',
  'Splitter',
  'Sorter',
  'Filter',
  'Validator',
];
